/**
 * Product pane for the family-columns board (WSRADM-0033).
 * Display filters / paging only — draft and PUT stay the full set (FamilyBoard).
 */
import { useEffect, useMemo, useState, type DragEvent } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { BoardFamily, BoardProduct } from '@/api/dimensions/types';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { EM_DASH, textOrDash } from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { Icon } from '@/theme';
import { distinctCategories, type Draft } from './boardModel';
import { CatalogueStatusChip } from './CatalogueStatusChip';
import { effectiveStatus, isFaded, isHideableRetired } from './catalogueStatus';
import { AllocateByCategoryDialog } from './dialogs';

export interface ProductPaneProps {
  products: BoardProduct[];
  families: BoardFamily[];
  draft: Draft;
  showRetired: boolean;
  onShowRetiredChange: (v: boolean) => void;
  addTo: (ids: number[], familySlug: string) => void;
  removeFrom: (ids: number[], familySlug: string) => void;
  onDragIdsChange: (ids: number[] | null) => void;
  selection: ReadonlySet<number>;
  onSelectionChange: (next: Set<number>) => void;
}

type QuickFilter = 'all' | 'unassigned' | 'inFamily' | 'retired';
type DurationFilter = 'any' | 'short' | 'month' | 'long' | 'none';

const PAGE_SIZES = [20, 50, 100] as const;

function matchesDuration(days: number | null, filter: DurationFilter): boolean {
  if (filter === 'any') return true;
  if (filter === 'none') return days === null;
  if (days === null) return false;
  if (filter === 'short') return days <= 7;
  if (filter === 'month') return days >= 8 && days <= 90;
  return days > 90;
}

export function ProductPane({
  products,
  families,
  draft,
  showRetired,
  onShowRetiredChange,
  addTo,
  removeFrom,
  onDragIdsChange,
  selection,
  onSelectionChange,
}: ProductPaneProps) {
  const { t } = useCopy();
  const [search, setSearch] = useState('');
  const [quick, setQuick] = useState<QuickFilter>('all');
  const [familyFilter, setFamilyFilter] = useState('');
  const [duration, setDuration] = useState<DurationFilter>('any');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [bulkFamily, setBulkFamily] = useState('');
  const [byCategoryOpen, setByCategoryOpen] = useState(false);
  const [draggingIds, setDraggingIds] = useState<number[] | null>(null);

  const categories = useMemo(() => distinctCategories(products), [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((product) => {
      const status = effectiveStatus(product);
      if (quick !== 'retired' && !showRetired && isHideableRetired(status)) return false;

      const slugs = draft.get(product.sourceProductId);
      const size = slugs?.size ?? 0;
      if (quick === 'unassigned' && size !== 0) return false;
      if (quick === 'inFamily' && size === 0) return false;
      if (quick === 'retired' && !isHideableRetired(status)) return false;

      if (familyFilter !== '' && slugs?.has(familyFilter) !== true) return false;
      if (!matchesDuration(product.durationDays, duration)) return false;
      if (category !== '' && !product.wstudioCategories.includes(category)) return false;

      if (q !== '') {
        const name = (product.name ?? '').toLowerCase();
        const cats = product.wstudioCategories.join(' ').toLowerCase();
        if (!name.includes(q) && !cats.includes(q)) return false;
      }
      return true;
    });
  }, [products, draft, showRetired, quick, familyFilter, duration, category, search]);

  const maxPage = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    setMenuId(null);
  }, [safePage, pageSize]);

  function resetPage() {
    setPage(0);
  }

  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const total = filtered.length;
  const showingA = total === 0 ? 0 : safePage * pageSize + 1;
  const showingB = Math.min((safePage + 1) * pageSize, total);

  function toggleSelected(id: number, on: boolean) {
    const next = new Set(selection);
    if (on) next.add(id);
    else next.delete(id);
    onSelectionChange(next);
  }

  function onRowDragStart(productId: number, event: DragEvent) {
    const ids = selection.has(productId) ? [...selection] : [productId];
    event.dataTransfer.setData('text/plain', ids.join(','));
    event.dataTransfer.effectAllowed = 'copy';
    setDraggingIds(ids);
    onDragIdsChange(ids);
  }

  function onRowDragEnd() {
    setDraggingIds(null);
    onDragIdsChange(null);
  }

  const quickChips: { id: QuickFilter; label: string }[] = [
    { id: 'all', label: t('config.pane.f_all', 'All') },
    { id: 'unassigned', label: t('config.pane.f_unassigned', 'Unassigned') },
    { id: 'inFamily', label: t('config.pane.f_inFamily', 'In a family') },
    { id: 'retired', label: t('config.pane.f_retired', 'Retired') },
  ];

  return (
    <Paper
      variant="outlined"
      data-testid="product-pane"
      sx={{
        width: 392,
        flex: 'none',
        position: 'sticky',
        top: 74,
        p: 1.5,
        '@media (max-width: 1180px)': {
          width: '100%',
          position: 'static',
        },
      }}
    >
      <TextField
        data-testid="pane-search"
        size="small"
        fullWidth
        placeholder={t('config.pane.searchPh', 'Search products…')}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          resetPage();
        }}
        slotProps={{ htmlInput: { 'data-testid': 'pane-search-input' } }}
        sx={{ mb: 1 }}
      />

      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {quickChips.map((chip) => (
          <Chip
            key={chip.id}
            data-testid={`pane-chip-${chip.id}`}
            size="small"
            label={chip.label}
            color={quick === chip.id ? 'primary' : 'default'}
            variant={quick === chip.id ? 'filled' : 'outlined'}
            onClick={() => {
              setQuick(chip.id);
              resetPage();
            }}
          />
        ))}
      </Stack>

      <TextField
        data-testid="pane-family-filter"
        select
        size="small"
        fullWidth
        label={t('config.pane.anyFamily', 'Any family')}
        value={familyFilter}
        onChange={(e) => {
          setFamilyFilter(e.target.value);
          resetPage();
        }}
        sx={{ mb: 1 }}
      >
        <MenuItem value="">{t('config.pane.anyFamily', 'Any family')}</MenuItem>
        {families.map((f) => (
          <MenuItem key={f.slug} value={f.slug}>
            {f.displayName}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        data-testid="pane-duration-filter"
        select
        size="small"
        fullWidth
        label={t('config.pane.dur_any', 'Any duration')}
        value={duration}
        onChange={(e) => {
          setDuration(e.target.value as DurationFilter);
          resetPage();
        }}
        sx={{ mb: 1 }}
      >
        <MenuItem value="any">{t('config.pane.dur_any', 'Any duration')}</MenuItem>
        <MenuItem value="short">{t('config.pane.dur_short', '≤ 7 days')}</MenuItem>
        <MenuItem value="month">{t('config.pane.dur_month', '8–90 days')}</MenuItem>
        <MenuItem value="long">{t('config.pane.dur_long', '> 90 days')}</MenuItem>
        <MenuItem value="none">{t('config.pane.dur_none', 'No duration')}</MenuItem>
      </TextField>

      <TextField
        data-testid="pane-category-filter"
        select
        size="small"
        fullWidth
        label={t('config.pane.anyCategory', 'Any category')}
        value={category}
        onChange={(e) => {
          setCategory(e.target.value);
          resetPage();
        }}
        sx={{ mb: 1 }}
      >
        <MenuItem value="">{t('config.pane.anyCategory', 'Any category')}</MenuItem>
        {categories.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>

      <FormControlLabel
        data-testid="pane-show-retired"
        control={
          <Checkbox
            checked={showRetired}
            onChange={(_e, on) => {
              onShowRetiredChange(on);
              resetPage();
            }}
          />
        }
        label={t('config.pane.showRetired', 'Show retired')}
        sx={{ mb: 1, display: 'flex' }}
      />

      {selection.size >= 1 ? (
        <Box
          data-testid="pane-selection-bar"
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.selected',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">
            {t('config.pane.selected', '{{count}} selected', { count: selection.size })}
          </Typography>
          <TextField
            data-testid="pane-add-to-family"
            select
            size="small"
            value={bulkFamily}
            label={t('config.pane.addToFamily', 'Add to family…')}
            onChange={(e) => {
              const slug = e.target.value;
              if (slug === '') return;
              addTo([...selection], slug);
              setBulkFamily('');
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('config.pane.addToFamily', 'Add to family…')}</MenuItem>
            {families.map((f) => (
              <MenuItem key={f.slug} value={f.slug}>
                {f.displayName}
              </MenuItem>
            ))}
          </TextField>
          <Button
            data-testid="pane-clear-selection"
            size="small"
            onClick={() => onSelectionChange(new Set())}
          >
            {t('config.pane.clearSel', 'Clear selection')}
          </Button>
        </Box>
      ) : null}

      <Box
        data-testid="pane-list"
        sx={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto', mb: 1 }}
      >
        {pageRows.length === 0 ? (
          <Typography data-testid="pane-no-match" color="text.secondary">
            {t('config.pane.noMatch', 'No products match.')}
          </Typography>
        ) : (
          pageRows.map((product) => {
            const id = product.sourceProductId;
            const selected = selection.has(id);
            const slugs = draft.get(id) ?? new Set<string>();
            const name = textOrDash(product.name);
            const meta = [
              textOrDash(product.productType),
              product.durationDays === null
                ? EM_DASH
                : t('config.board.days', '{{count}} days', { count: product.durationDays }),
              product.hasPendingVisits === true
                ? t('config.pane.visitPack', 'Visit pack')
                : null,
              product.sourceSystem === 'LEGACY' ? t('config.pane.legacy', 'Legacy') : null,
            ]
              .filter((part) => part !== null)
              .join(' · ');
            const catsLine = product.wstudioCategories.join(', ');
            const familyChips = families.filter((f) => slugs.has(f.slug));
            const isDragging = draggingIds?.includes(id) === true;
            const status = effectiveStatus(product);
            const faded = isFaded(status);

            return (
              <Box
                key={id}
                data-testid={`pane-row-${id}`}
                draggable
                onDragStart={(e) => onRowDragStart(id, e)}
                onDragEnd={onRowDragEnd}
                sx={{
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  opacity: isDragging ? 0.45 : faded ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                }}
              >
                <Checkbox
                  data-testid={`pane-row-check-${id}`}
                  size="small"
                  checked={selected}
                  onChange={(_e, on) => toggleSelected(id, on)}
                  slotProps={{ input: { 'aria-label': name } }}
                  sx={{ p: 0.5 }}
                />
                <Box
                  aria-hidden
                  sx={{
                    cursor: 'grab',
                    color: 'text.secondary',
                    pt: 0.5,
                    userSelect: 'none',
                  }}
                >
                  <Icon name="drag_indicator" sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography
                      sx={{ color: faded ? 'text.secondary' : 'text.primary' }}
                    >
                      {name}
                    </Typography>
                    <CatalogueStatusChip status={status} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {meta}
                  </Typography>
                  {catsLine !== '' ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {catsLine}
                    </Typography>
                  ) : null}
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5, gap: 0.5 }}>
                    {familyChips.length === 0 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        {t('config.pane.noFamilies', 'Not in a family')}
                      </Typography>
                    ) : (
                      familyChips.map((f) => {
                        const resolved = familyColorOrNeutral(f.color);
                        return (
                          <Chip
                            key={f.slug}
                            size="small"
                            label={f.displayName}
                            sx={
                              resolved !== null
                                ? { bgcolor: resolved.bg, color: resolved.fg }
                                : undefined
                            }
                            variant={resolved === null ? 'outlined' : 'filled'}
                          />
                        );
                      })
                    )}
                  </Stack>
                  {menuId === id ? (
                    <Box
                      data-testid={`pane-menu-${id}`}
                      sx={{
                        mt: 1,
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {families.map((f) => {
                          const inFamily = slugs.has(f.slug);
                          return (
                            <Chip
                              key={f.slug}
                              data-testid={`pane-menu-toggle-${id}-${f.slug}`}
                              size="small"
                              label={f.displayName}
                              color={inFamily ? 'primary' : 'default'}
                              variant={inFamily ? 'filled' : 'outlined'}
                              icon={inFamily ? <Icon name="check" sx={{ fontSize: 16 }} /> : undefined}
                              onClick={() => {
                                if (inFamily) removeFrom([id], f.slug);
                                else addTo([id], f.slug);
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : null}
                </Box>
                <IconButton
                  size="small"
                  data-testid={`pane-assign-${id}`}
                  aria-label={t('config.pane.assign', 'Assign')}
                  onClick={() => setMenuId((cur) => (cur === id ? null : id))}
                >
                  <Icon name="more_vert" />
                </IconButton>
              </Box>
            );
          })
        )}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
      >
        <Typography data-testid="pane-showing" variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {t('config.pane.showing', 'Showing {{a}}–{{b}} of {{n}}', {
            a: showingA,
            b: showingB,
            n: total,
          })}
        </Typography>
        <TextField
          data-testid="pane-page-size"
          select
          size="small"
          label={t('config.pane.perPage', 'Per page')}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          sx={{ minWidth: 100 }}
        >
          {PAGE_SIZES.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        <Button
          data-testid="pane-prev"
          size="small"
          disabled={safePage <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          {t('config.pane.prev', 'Prev')}
        </Button>
        <Button
          data-testid="pane-next"
          size="small"
          disabled={safePage >= maxPage}
          onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
        >
          {t('config.pane.next', 'Next')}
        </Button>
      </Stack>

      <Button
        data-testid="pane-by-category"
        size="small"
        onClick={() => setByCategoryOpen(true)}
        disabled={categories.length === 0 || families.length === 0}
      >
        {t('config.pane.byCategory', 'Allocate by WStudio category…')}
      </Button>

      <AllocateByCategoryDialog
        open={byCategoryOpen}
        categories={categories}
        families={families}
        onClose={() => setByCategoryOpen(false)}
        onApply={(cat, familySlug) => {
          const ids = products
            .filter((p) => p.wstudioCategories.includes(cat))
            .map((p) => p.sourceProductId);
          if (ids.length > 0) addTo(ids, familySlug);
          setByCategoryOpen(false);
        }}
      />
    </Paper>
  );
}
